// F7: modal, drawer, confirmação e toast. Cor só por token; foco preso no diálogo; Esc fecha.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, X } from 'lucide-react';

function useEsc(aberto: boolean, fechar: () => void) {
  useEffect(() => {
    if (!aberto) return;
    const f = (e: KeyboardEvent) => e.key === 'Escape' && fechar();
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [aberto, fechar]);
}

const FOCAVEIS = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
let abertos = 0;

/**
 * VA-02: foco preso no diálogo. Enquanto houver diálogo aberto, a aplicação (#raiz) fica inert, e o Tab e o
 * Shift+Tab circulam só entre os focáveis do diálogo de cima. Ao fechar, o foco volta para quem abriu.
 */
function useFoco(aberto: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const antes = document.activeElement as HTMLElement | null;
    // VB15-01 (rodada 15): campo desabilitado (ex.: lista de opções ainda carregando) não recebe foco; sem campo
    // habilitado, o foco vai para o próprio painel do diálogo (tabIndex -1), nunca fica no body com a aplicação inert.
    const alvo = ref.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([data-fechar])') ?? ref.current;
    alvo?.focus();
    const raiz = document.getElementById('raiz');
    abertos += 1;
    raiz?.setAttribute('inert', '');
    const prender = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !ref.current) return;
      const dialogos = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (dialogos[dialogos.length - 1] !== ref.current) return;
      const lista = [...ref.current.querySelectorAll<HTMLElement>(FOCAVEIS)].filter((x) => x.offsetParent !== null);
      if (!lista.length) return;
      const primeiro = lista[0]!;
      const ultimo = lista[lista.length - 1]!;
      const atual = document.activeElement;
      if (e.shiftKey && (atual === primeiro || !ref.current.contains(atual))) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (atual === ultimo || !ref.current.contains(atual))) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    document.addEventListener('keydown', prender);
    return () => {
      document.removeEventListener('keydown', prender);
      abertos -= 1;
      if (abertos <= 0) {
        abertos = 0;
        raiz?.removeAttribute('inert');
      }
      antes?.focus();
    };
  }, [aberto]);
  return ref;
}

export function Modal({ aberto, aoFechar, titulo, children, rodape, largura = 'max-w-lg' }: { aberto: boolean; aoFechar: () => void; titulo: string; children: ReactNode; rodape?: ReactNode; largura?: string }) {
  const { t } = useTranslation();
  useEsc(aberto, aoFechar);
  const ref = useFoco(aberto);
  if (!aberto) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <button type="button" aria-label={t('acao.fechar')} data-fechar onClick={aoFechar} className="absolute inset-0 bg-fundo/70" />
      <div ref={ref} role="dialog" aria-modal="true" aria-label={titulo} tabIndex={-1} className={`relative flex max-h-full w-full ${largura} flex-col rounded-t-2xl border border-borda bg-sup-1 shadow-2xl sm:rounded-2xl`}>
        <div className="flex items-center justify-between gap-4 border-b border-borda px-5 py-4">
          <h2 className="text-lg font-semibold">{titulo}</h2>
          <button type="button" data-fechar onClick={aoFechar} aria-label={t('acao.fechar')} className="grid size-11 place-items-center rounded-lg text-tinta-3 sm:size-8 hover:bg-sup-2 hover:text-tinta-1">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-5">{children}</div>
        {rodape && <div className="flex flex-wrap justify-end gap-2 border-t border-borda px-5 py-4">{rodape}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({ aberto, aoFechar, titulo, children }: { aberto: boolean; aoFechar: () => void; titulo: string; children: ReactNode }) {
  const { t } = useTranslation();
  useEsc(aberto, aoFechar);
  const ref = useFoco(aberto);
  if (!aberto) return null;
  return createPortal(
    <div className="fixed inset-0 z-40">
      <button type="button" aria-label={t('acao.fechar')} data-fechar onClick={aoFechar} className="absolute inset-0 bg-fundo/60" />
      <aside ref={ref} role="dialog" aria-modal="true" aria-label={titulo} tabIndex={-1} className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col border-l border-borda bg-sup-1 shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b border-borda px-5 py-4">
          <h2 title={titulo} className="min-w-0 break-words text-lg font-semibold">{titulo}</h2>
          <button type="button" data-fechar onClick={aoFechar} aria-label={t('acao.fechar')} className="grid size-11 place-items-center rounded-lg text-tinta-3 sm:size-8 hover:bg-sup-2 hover:text-tinta-1">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </aside>
    </div>,
    document.body,
  );
}

export function Confirmar({ aberto, aoFechar, aoConfirmar, titulo, texto, perigo = false, ocupado = false }: { aberto: boolean; aoFechar: () => void; aoConfirmar: () => void; titulo: string; texto: string; perigo?: boolean; ocupado?: boolean }) {
  const { t } = useTranslation();
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      largura="max-w-md"
      rodape={
        <>
          <Botao variante="secundario" onClick={aoFechar}>{t('acao.cancelar')}</Botao>
          <Botao variante={perigo ? 'perigo' : 'primario'} onClick={aoConfirmar} disabled={ocupado}>{t('acao.confirmar')}</Botao>
        </>
      }
    >
      <p className="text-sm text-tinta-2">{texto}</p>
    </Modal>
  );
}

export function Botao({ variante = 'primario', className = '', ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variante?: 'primario' | 'secundario' | 'perigo' | 'fantasma' }) {
  const v = {
    primario: 'bg-acento text-acento-tinta hover:bg-acento-forte',
    secundario: 'border border-borda text-tinta-1 hover:bg-sup-2',
    // VB25-01 (rodada 25): no escuro o --perigo é claro; o texto usa --tinta-sobre-tom (escuro no escuro, branco no claro).
    perigo: 'bg-perigo text-tinta-sobre-tom hover:opacity-90',
    fantasma: 'text-tinta-2 hover:bg-sup-2 hover:text-tinta-1',
  }[variante];
  return <button type="button" {...p} className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors disabled:opacity-50 ${v} ${className}`} />;
}

// ---- Toast ----
interface ItemToast {
  id: number;
  tipo: 'sucesso' | 'erro';
  texto: string;
}
const CtxToast = createContext<(tipo: ItemToast['tipo'], texto: string) => void>(() => undefined);

export function ProvedorToast({ children }: { children: ReactNode }) {
  const [itens, setItens] = useState<ItemToast[]>([]);
  const avisar = useCallback((tipo: ItemToast['tipo'], texto: string) => {
    const id = Date.now() + Math.random();
    setItens((x) => [...x, { id, tipo, texto }]);
    setTimeout(() => setItens((x) => x.filter((i) => i.id !== id)), 4500);
  }, []);
  return (
    <CtxToast.Provider value={avisar}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-end gap-2 sm:inset-x-auto sm:right-6">
        {itens.map((i) => (
          <div key={i.id} role={i.tipo === 'erro' ? 'alert' : 'status'} className="pointer-events-auto flex max-w-sm items-start gap-2 rounded-xl border border-borda bg-sup-2 px-4 py-3 text-sm shadow-xl">
            {i.tipo === 'sucesso' ? <CircleCheck size={18} className="mt-0.5 shrink-0 text-sucesso" /> : <CircleAlert size={18} className="mt-0.5 shrink-0 text-perigo" />}
            <span>{i.texto}</span>
          </div>
        ))}
      </div>
    </CtxToast.Provider>
  );
}

export const useToast = () => useContext(CtxToast);
